import type { ActivityContext, ActivityHandle } from '../../core/activity';
import levelsData from './levels.json';

interface DataEntry {
  subLevels: number;
  numberOfItemType: number;
  numberOfItemMax: number;
}

interface Level {
  level: number;
  data: DataEntry[];
}

interface LevelsFile {
  levels: Level[];
}

const LEVELS = (levelsData as LevelsFile).levels;
const FRUITS = ['🍇', '🍑', '🍓', '🍉'];

interface Item {
  id: number;
  type: number; // index in FRUITS
  x: number; // percent 0-100
  y: number;
}

function randInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function pickTypes(n: number): number[] {
  const all = [0, 1, 2, 3];
  for (let i = all.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = all[i]!;
    all[i] = all[j]!;
    all[j] = tmp;
  }
  return all.slice(0, n);
}

export function mountEnumerate(ctx: ActivityContext): ActivityHandle {
  const level = LEVELS.find((l) => l.level === ctx.level) ?? LEVELS[0];
  if (!level) {
    ctx.root.innerHTML = '<p>Nível não encontrado.</p>';
    return { unmount: () => { ctx.root.innerHTML = ''; } };
  }

  const totalSubs = level.data.reduce((acc, d) => acc + d.subLevels, 0);
  let dataIdx = 0;
  let subWithinData = 0;
  let correctCount = 0;
  let items: Item[] = [];
  let typesPresent: number[] = []; // types used in this sublevel, ordered
  let answers: Record<number, number | null> = {};
  let buttonsBlocked = false;

  const page = document.createElement('div');
  page.className = 'activity-page';
  page.innerHTML = `
    <header class="activity-bar">
      <button class="activity-bar__back" type="button" aria-label="Voltar ao hub">← Voltar</button>
      <div class="activity-bar__title">Conte os frutos</div>
      <div class="activity-bar__level">Nível ${level.level} de ${LEVELS.length}</div>
    </header>
    <section class="activity-stage enum">
      <div class="feedback" aria-live="polite"></div>
      <p class="enum__progress" aria-live="polite"></p>
      <div class="enum__board">
        <div class="enum__scene" aria-label="Cena com frutos"></div>
        <div class="enum__panel" role="group" aria-label="Respostas"></div>
      </div>
      <p class="enum__hint">Conte quantos de cada tipo aparecem. Arraste pra organizar se quiser.</p>
      <div class="enum__actions">
        <button class="btn btn--primary enum__confirm" type="button">Confirmar</button>
      </div>
    </section>
  `;
  ctx.root.appendChild(page);

  const progressEl = page.querySelector<HTMLElement>('.enum__progress')!;
  const sceneEl = page.querySelector<HTMLElement>('.enum__scene')!;
  const panelEl = page.querySelector<HTMLElement>('.enum__panel')!;
  const feedback = page.querySelector<HTMLElement>('.feedback')!;
  const confirmBtn = page.querySelector<HTMLButtonElement>('.enum__confirm')!;
  page.querySelector<HTMLElement>('.activity-bar__back')!.addEventListener('click', () => ctx.onExit());
  confirmBtn.addEventListener('click', validate);

  function startSubLevel(): void {
    const cfg = level.data[dataIdx]!;
    typesPresent = pickTypes(cfg.numberOfItemType);
    items = [];
    answers = {};
    let nextId = 0;
    for (const t of typesPresent) {
      const n = randInt(1, cfg.numberOfItemMax);
      answers[t] = null;
      for (let i = 0; i < n; i++) {
        items.push({ id: nextId++, type: t, x: randInt(8, 92), y: randInt(8, 92) });
      }
    }
    buttonsBlocked = false;
    renderAll();
  }

  function countItemsByType(t: number): number {
    return items.filter((i) => i.type === t).length;
  }

  function renderAll(): void {
    renderProgress();
    renderScene();
    renderPanel();
  }

  function renderProgress(): void {
    const overall = level.data.slice(0, dataIdx).reduce((acc, d) => acc + d.subLevels, 0) + subWithinData;
    progressEl.textContent = `Pergunta ${overall + 1} de ${totalSubs}`;
  }

  function renderScene(): void {
    sceneEl.innerHTML = '';
    for (const it of items) {
      const el = document.createElement('div');
      el.className = 'enum__item';
      el.textContent = FRUITS[it.type] ?? '🍓';
      el.style.left = `${it.x}%`;
      el.style.top = `${it.y}%`;
      el.dataset['itemId'] = String(it.id);
      attachDrag(el, it);
      sceneEl.appendChild(el);
    }
  }

  function attachDrag(el: HTMLElement, it: Item): void {
    let active = false;
    let offsetX = 0;
    let offsetY = 0;
    let pointerId = -1;

    const onDown = (e: PointerEvent): void => {
      if (buttonsBlocked) return;
      e.preventDefault();
      active = true;
      pointerId = e.pointerId;
      el.setPointerCapture(pointerId);
      el.classList.add('enum__item--dragging');
      const rect = sceneEl.getBoundingClientRect();
      offsetX = e.clientX - (rect.left + (it.x / 100) * rect.width);
      offsetY = e.clientY - (rect.top + (it.y / 100) * rect.height);
    };
    const onMove = (e: PointerEvent): void => {
      if (!active) return;
      const rect = sceneEl.getBoundingClientRect();
      let nx = ((e.clientX - rect.left - offsetX) / rect.width) * 100;
      let ny = ((e.clientY - rect.top - offsetY) / rect.height) * 100;
      nx = Math.max(4, Math.min(96, nx));
      ny = Math.max(4, Math.min(96, ny));
      it.x = nx;
      it.y = ny;
      el.style.left = `${nx}%`;
      el.style.top = `${ny}%`;
    };
    const onUp = (): void => {
      if (!active) return;
      active = false;
      el.classList.remove('enum__item--dragging');
      try { el.releasePointerCapture(pointerId); } catch { /* no-op */ }
    };
    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onUp);
  }

  function renderPanel(): void {
    panelEl.innerHTML = '';
    for (const t of typesPresent) {
      const wrap = document.createElement('div');
      wrap.className = 'enum__answer';
      wrap.dataset['type'] = String(t);
      const icon = document.createElement('div');
      icon.className = 'enum__answer-icon';
      icon.textContent = FRUITS[t] ?? '?';
      const input = document.createElement('input');
      input.type = 'number';
      input.inputMode = 'numeric';
      input.className = 'enum__answer-input';
      input.min = '0';
      input.max = '20';
      if (answers[t] !== null && answers[t] !== undefined) input.value = String(answers[t]);
      input.addEventListener('input', () => {
        const v = parseInt(input.value, 10);
        answers[t] = Number.isFinite(v) ? v : null;
      });
      wrap.appendChild(icon);
      wrap.appendChild(input);
      panelEl.appendChild(wrap);
    }
  }

  function validate(): void {
    if (buttonsBlocked) return;
    let allOk = true;
    for (const t of typesPresent) {
      const expected = countItemsByType(t);
      const got = answers[t];
      const wrap = panelEl.querySelector<HTMLElement>(`.enum__answer[data-type="${t}"]`);
      if (!wrap) continue;
      wrap.classList.remove('enum__answer--correct', 'enum__answer--wrong');
      if (got === expected) {
        wrap.classList.add('enum__answer--correct');
      } else {
        wrap.classList.add('enum__answer--wrong');
        allOk = false;
      }
    }
    if (allOk) {
      buttonsBlocked = true;
      ctx.audio.play('correct');
      showFeedback('ok');
      correctCount++;
      window.setTimeout(() => {
        advance();
      }, 800);
    } else {
      ctx.audio.play('wrong');
      showFeedback('err');
    }
  }

  function advance(): void {
    subWithinData++;
    if (subWithinData >= level.data[dataIdx]!.subLevels) {
      subWithinData = 0;
      dataIdx++;
    }
    if (dataIdx >= level.data.length) finishLevel();
    else startSubLevel();
  }

  function showFeedback(kind: 'ok' | 'err'): void {
    feedback.textContent = kind === 'ok' ? '✓' : '✗';
    feedback.className = `feedback feedback--show feedback--${kind}`;
    window.setTimeout(() => feedback.classList.remove('feedback--show'), 500);
  }

  function finishLevel(): void {
    ctx.audio.play('win');
    ctx.onComplete(level.level, correctCount);
    const stage = page.querySelector<HTMLElement>('.activity-stage')!;
    stage.innerHTML = `
      <h2 class="activity-finish__title">Nível ${level.level} concluído!</h2>
      <p class="activity-finish__text">Você contou ${correctCount} de ${totalSubs} cenas.</p>
      <div class="activity-finish__actions">
        <button class="btn btn--secondary" type="button" data-act="hub">Voltar ao hub</button>
        ${level.level < LEVELS.length
          ? `<button class="btn btn--primary" type="button" data-act="next">Próximo nível</button>`
          : ''}
      </div>
    `;
    stage.querySelector<HTMLElement>('[data-act="hub"]')!.addEventListener('click', () => ctx.onExit());
    const next = stage.querySelector<HTMLElement>('[data-act="next"]');
    if (next) {
      next.addEventListener('click', () => {
        window.location.hash = `/enumerate/${level.level + 1}`;
      });
    }
  }

  startSubLevel();

  return {
    unmount(): void {
      ctx.root.innerHTML = '';
    },
  };
}
